import React from 'react'
import styled from 'styled-components'
import {
  Box,
  SxProp,
  VisuallyHidden,
  Button,
  Logo,
  ButtonProps,
  Link as FuegokitLink,
  LinkProps,
  Text,
  themeGet,
  sx
} from '../..'

// --------------------------------------------------------
// Header NavTitle
export type HeaderNavTitleProps = {
  /**
   * The children to pass into the Global Nav wrapper
   */
  children?: React.ReactNode
  /**
   * A url string for a product or logo image to use in the home link in .png format. Falls back to an Appfire logo if no img url is provided.
   */
  appLogoUrl?: string
  /**
   * You must include accessible alt text for a logo image url. See: https://www.w3.org/WAI/tutorials/images/
   */
  appLogoImgAlt?: string
  /**
   * Optional subtitle
   */
  subtitle?: string
  /**
   * Title of the application
   */
  title?: string
  /**
   * If the the containing nav is resizable, use the resize prop for granular control of certain elements
   */
  resized?: boolean
  /**
   * Optional href if navigating to an external site
   */
  href?: string
  /**
   * Event handler called when the user selects a link (via mouse or keyboard).
   */
  onSelect?: (event: Event) => void
}
/**
 * Rule Builder Nav Title. Must be rendered inside a GlobalHeader.Nav component
 */
const HeaderNavTitle = ({
  href,
  appLogoUrl,
  appLogoImgAlt,
  subtitle,
  title,
  resized,
  onSelect,
  ...props
}: HeaderNavTitleProps) => {
  if (appLogoUrl && !appLogoImgAlt) {
    console.warn(
      `You must include accessible alt text to describe your logo image. Please refer to https://www.w3.org/WAI/tutorials/images/.`
    )
  }
  return (
    <HeaderNavTitleLink href={href} onSelect={onSelect} {...props}>
      <Box>
        <VisuallyHidden id="mainmenulabel">Main menu</VisuallyHidden>
        {appLogoUrl ? (
          <Box
            as="img"
            src={appLogoUrl}
            alt={appLogoImgAlt}
            style={subtitle && !resized ? {height: '44px', width: '44px'} : {height: '20px', width: '20px'}}
            sx={{display: 'block'}}
          />
        ) : (
          <Logo variant="solid" size={20} color={`inherit`} aria-hidden="true" />
        )}
      </Box>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start'}}>
        <StyledNavTitle>{title}</StyledNavTitle>
        {subtitle && !resized && <StyledNavSubtitle>{subtitle}</StyledNavSubtitle>}
      </Box>
    </HeaderNavTitleLink>
  )
}
const StyledNavTitle = styled(Text)`
  text-decoration: none;
  font-size: ${themeGet('fontSizes.4')};
  font-weight: ${themeGet('fontWeights.semibold')};
`
const StyledNavSubtitle = styled(Text)`
  text-decoration: none;
  font-size: ${themeGet('fontSizes.2')};
  color: ${themeGet('colors.text.subtle')};
  font-weight: ${themeGet('fontWeights.medium')};
`
// --------------------------------------------------------
// Header Nav Title Link
export type HeaderNavTitleLinkProps = {
  /**
   * the label to display for the nav item
   */
  children?: React.ReactNode
  /**
   * Links in GlobalHeader Nav can be an anchor tag or a route, so these can be href strings or route strings
   */
  href?: string
  /**
   * Used to identify the link as the currently active page.
   */
  active?: boolean
  /**
   * Event handler called when the user selects a link (via mouse or keyboard).
   */
  onSelect?: (event: Event) => void
}
/**
 * Navigation link item for a Header component
 */
const HeaderNavTitleLink = ({children, href, active, onSelect, ...props}: HeaderNavTitleLinkProps) => {
  if (href && onSelect) {
    throw new Error(
      `HeaderNav Links take an onSelect or an href prop, but not both. See: https://fuegokit.design/react/patterns/global-header.`
    )
  }
  if (href) {
    return (
      <StyledNavLink href={href} {...props}>
        {children}
      </StyledNavLink>
    )
  } else if (onSelect) {
    return (
      <StyledNavButton onClick={onSelect} {...props}>
        {children}
      </StyledNavButton>
    )
  } else {
    throw new Error(
      `Provide the NavLink an onSelect or an href prop. See: https://fuegokit.design/react/patterns/global-header.`
    )
  }
}
const StyledNavButton = styled.button<HeaderNavTitleLinkProps & ButtonProps & SxProp>`
  appearance: none;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;

  text-decoration: none;
  color: ${themeGet('colors.text.default')};
  display: inline-flex;
  align-items: center;
  gap: ${themeGet('space.2')};
  &:hover {
    text-decoration: none;
  }
  &:focus,
  .focus-visible {
    outline: none;
    transition-duration: 0s, 0.2s;
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
    border-radius: ${themeGet('radii.2')};
  }
  &:focus:not(:focus-visible):not(.focus-visible) {
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
    border-radius: ${themeGet('radii.2')};
  }
  ${sx}
`
const StyledNavLink = styled(FuegokitLink)<HeaderNavTitleLinkProps & LinkProps & SxProp>`
  text-decoration: none;
  color: ${themeGet('colors.text.default')};
  display: inline-flex;
  align-items: center;
  gap: ${themeGet('space.2')};
  &:hover {
    text-decoration: none;
  }
  &:focus,
  .focus-visible {
    outline: none;
    transition-duration: 0s, 0.2s;
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
    border-radius: ${themeGet('radii.2')};
  }
  &:focus:not(:focus-visible):not(.focus-visible) {
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
    border-radius: ${themeGet('radii.2')};
  }
  ${sx}
`
export default HeaderNavTitle
